import {
  canReadCompanyEarnings,
  hasAdminOrSuperAdminRole,
  hasFinanceAdminOrSuperAdminRole,
} from '../constants/roles.constants';
import {
  WORK_REPORT_INVOICE_STATUS,
  type WorkReportInvoiceStatus,
} from '../constants/work-report.constants';

/** Kontext stránky výkazu — určuje fakturační UX (vlastní / cizí uživatel / polygon). */
export type WorkReportBillingScope = 'own' | 'managed' | 'polygon' | 'company';

export interface WorkReportBillingPermissionInput {
  viewerRoles: string[] | undefined | null;
  viewerEmail: string | null | undefined;
  scope: WorkReportBillingScope;
}

export function canShowWorkReportRowSelection(input: WorkReportBillingPermissionInput): boolean {
  if (hasFinanceAdminOrSuperAdminRole(input.viewerRoles)) return true;
  if (input.scope === 'company') return canReadCompanyEarnings(input.viewerRoles);
  return input.scope === 'own';
}

/**
 * Stejná faktura jako Výkaz práce (`payment_status` + `invoice_id`).
 * Správce firmy smí řádky své firmy, ne cizí. Prázdný seznam = nikdo.
 */
export function mayInvoiceWorkReportOwner(
  ownerEmail: string,
  actorEmail: string,
  options?: { allowAnyOwner?: boolean; allowedOwnerEmails?: readonly string[] | null },
): boolean {
  if (options?.allowAnyOwner) return true;
  const owner = String(ownerEmail ?? '').trim().toLowerCase();
  if (!owner) return false;
  if (options?.allowedOwnerEmails) {
    const allowed = new Set(
      options.allowedOwnerEmails.map((email) => String(email ?? '').trim().toLowerCase()).filter(Boolean),
    );
    return allowed.has(owner);
  }
  return owner === String(actorEmail ?? '').trim().toLowerCase();
}

/** Označit neproplacené řádky jako vyfakturované (vytvoří fakturu). */
export function canShowWorkReportInvoiceAction(input: WorkReportBillingPermissionInput): boolean {
  return canShowWorkReportRowSelection(input);
}

/** @deprecated Use {@link canShowWorkReportInvoiceAction}. */
export function canShowWorkReportSettleAction(input: WorkReportBillingPermissionInput): boolean {
  return canShowWorkReportInvoiceAction(input);
}

/** Vrácení vyfakturovaných řádků na neproplacené — jen finance / admin (ne terén). */
export function canShowWorkReportBulkUnpaidAction(input: WorkReportBillingPermissionInput): boolean {
  return hasFinanceAdminOrSuperAdminRole(input.viewerRoles);
}

/** @deprecated Use {@link canShowWorkReportBulkUnpaidAction}. */
export function canShowWorkReportAdminBulkUnpaidAction(input: WorkReportBillingPermissionInput): boolean {
  return canShowWorkReportBulkUnpaidAction(input);
}

/** @deprecated Use {@link canShowWorkReportBulkUnpaidAction}. */
export function canShowWorkReportAdminBulkActions(input: WorkReportBillingPermissionInput): boolean {
  return canShowWorkReportBulkUnpaidAction(input);
}

export function canShowWorkReportInvoiceHistory(input: WorkReportBillingPermissionInput): boolean {
  if (input.scope === 'polygon') return true;
  if (input.scope === 'own') return true;
  if (input.scope === 'company') return canReadCompanyEarnings(input.viewerRoles);
  return hasFinanceAdminOrSuperAdminRole(input.viewerRoles);
}

/** Proplaceno u faktury — jen admin / superadmin (ne manažer), včetně vlastního výkazu. */
export function canPayWorkReportInvoicePermission(input: WorkReportBillingPermissionInput): boolean {
  return hasAdminOrSuperAdminRole(input.viewerRoles);
}

/** Vrácení proplacené faktury na vyfakturované — jen admin / superadmin. */
export function canRevertPaidWorkReportInvoicePermission(input: WorkReportBillingPermissionInput): boolean {
  return hasAdminOrSuperAdminRole(input.viewerRoles);
}

/** Zrušení nevyřízené faktury (řádky zpět na neproplacené) — jen admin / superadmin. */
export function canDeleteWorkReportInvoicePermission(
  input: WorkReportBillingPermissionInput,
  summary: { createdBy?: string | null; status: WorkReportInvoiceStatus | string },
): boolean {
  if (summary.status !== WORK_REPORT_INVOICE_STATUS.PENDING) return false;
  return hasAdminOrSuperAdminRole(input.viewerRoles);
}

export function canPayWorkReportInvoiceSummary(
  input: WorkReportBillingPermissionInput,
  summary: { status: WorkReportInvoiceStatus | string },
): boolean {
  return (
    canPayWorkReportInvoicePermission(input) &&
    summary.status === WORK_REPORT_INVOICE_STATUS.PENDING
  );
}

export function canRevertPaidWorkReportInvoiceSummary(
  input: WorkReportBillingPermissionInput,
  summary: { status: WorkReportInvoiceStatus | string },
): boolean {
  return (
    canRevertPaidWorkReportInvoicePermission(input) &&
    summary.status === WORK_REPORT_INVOICE_STATUS.PAID
  );
}
